import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { Cookies } from 'react-cookie';
import { CARESENSE_CONFIG } from './config';
import {
  validateCaresenseToken,
  getTokenFromStorage,
} from '../AnyfitMall/common';
import CaresenseLayout, {
  StickyLink,
} from 'components/Caresense/CaresenseLayout';
import CaresenseLoading from 'components/Caresense/CaresenseLoading';
import { navigateToCaresenseError } from './caresenseErrors';
import { getBranding, getDivisionFromToken } from 'utils/branding';
import { palette } from '../AnyfitMall/theme';

const cookies = new Cookies();

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 300px;
  padding: 2rem 1rem;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #f6ffed;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  &::before {
    content: '';
    width: 32px;
    height: 32px;
    background-color: #52c41a;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E")
      center/contain no-repeat;
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E")
      center/contain no-repeat;
  }
`;

const SuccessTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${palette.Gray900};
  margin-bottom: 0.75rem;
  font-family: 'Noto Sans KR', sans-serif;
`;

const SuccessMessage = styled.p`
  font-size: 0.938rem;
  color: ${palette.Gray600};
  line-height: 1.6;
  font-family: 'Noto Sans KR', sans-serif;
`;

/**
 * 케어센스 OAuth 콜백 처리
 * 전체 플로우: Login(0-15%) → LogoutCallback(15-30%) → OAuth → Callback(30-100%)
 */
export default function CaresenseCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [progress, setProgress] = useState(30);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    const handleCallback = async () => {
      const isDev = ['local', 'dev'].includes(process.env.REACT_APP_ENV);

      try {
        setProgress(40);

        // 1. 콜백 파라미터 추출
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          throw new Error(`케어센스 인증 실패: ${error}`);
        }

        if (!code) {
          throw new Error('인증 코드가 제공되지 않았습니다.');
        }

        setProgress(50);

        // 2. State 검증 (CSRF 방지)
        const storedState = cookies.get('caresense_oauth_state');

        if (isDev) {
          console.log('[Caresense] State 검증:', {
            received: state,
            stored: storedState,
            match: state === storedState,
          });
        }

        if (!storedState || state !== storedState) {
          console.error('[Caresense] State 불일치 에러', {
            received: state,
            stored: storedState,
            cookies: document.cookie,
          });
          navigateToCaresenseError(navigate);
          return;
        }

        setProgress(60);

        // 3. 초기 토큰 조회 및 검증
        const initialToken = getTokenFromStorage();
        if (!initialToken) {
          throw new Error('초기 토큰을 찾을 수 없습니다.');
        }

        const validationResult = await validateCaresenseToken(initialToken);
        if (!validationResult.result) {
          throw new Error('토큰이 유효하지 않습니다.');
        }

        setProgress(70);

        // 4. 백엔드로 전송할 데이터 구성
        const requestData = {
          code,
          state,
          redirectUri: new URL(CARESENSE_CONFIG.redirectUri).href,
        };

        // OIDC 모드일 때 nonce 추가
        if (CARESENSE_CONFIG.useOpenIdConnect) {
          const storedNonce = cookies.get('caresense_oauth_nonce');

          if (storedNonce) {
            requestData.nonce = storedNonce;
          } else {
            console.error('[Caresense] Nonce 누락 (OIDC 모드)');
          }
        }

        if (isDev) {
          console.log('[Caresense] 백엔드 전송 데이터:', {
            hasCode: !!requestData.code,
            hasState: !!requestData.state,
            hasNonce: !!requestData.nonce,
          });
        }

        setProgress(80);

        // 5. 백엔드 API 호출
        const response = await axios.post(
          `${CARESENSE_CONFIG.apiBaseUrl}/api/caresense/oauth/callback`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${validationResult.token}`,
            },
            timeout: 30000,
          },
        );

        if (isDev) {
          console.log('[Caresense] 백엔드 응답:', {
            success: response.data.success,
            message: response.data.message,
          });
        }

        setProgress(90);

        if (response.data.success) {
          setStatus('success');

          // 6. OAuth 세션 정리
          cookies.remove('caresense_oauth_state', { path: '/' });
          cookies.remove('caresense_oauth_nonce', { path: '/' });

          // 7. B2C만 2초 후 자동 리다이렉트 (B2B는 버튼 클릭으로만 이동)
          if (getDivisionFromToken() !== 'B2B') {
            redirectTimerRef.current = setTimeout(() => {
              window.location.href = CARESENSE_CONFIG.finalRedirectUrl;
            }, 2000);
          }
        } else {
          throw new Error(
            response.data.message ||
              '케어센스 연동 처리 중 오류가 발생했습니다.',
          );
        }
      } catch (error) {
        console.error('[Caresense] 오류 발생:', error.message || error);

        // OAuth 세션 정리
        cookies.remove('caresense_oauth_state', { path: '/' });
        cookies.remove('caresense_oauth_nonce', { path: '/' });

        // 에러 페이지로 리다이렉트
        navigateToCaresenseError(navigate);
      }
    };

    handleCallback();

    return () => {
      clearTimeout(redirectTimerRef.current);
    };
  }, [searchParams, navigate]);

  const branding = getBranding();

  const handleGoToApp = (e) => {
    e.preventDefault();
    clearTimeout(redirectTimerRef.current);
    window.location.href = CARESENSE_CONFIG.finalRedirectUrl;
  };

  const isB2B = getDivisionFromToken() === 'B2B';

  if (status === 'success') {
    return (
      <CaresenseLayout
        footer={
          <StickyLink
            href={CARESENSE_CONFIG.finalRedirectUrl}
            onClick={handleGoToApp}
          >
            {branding.name} 앱으로 이동
          </StickyLink>
        }
      >
        <SuccessContainer>
          <SuccessIcon />
          <SuccessTitle>연동 완료</SuccessTitle>
          <SuccessMessage>
            {isB2B
              ? '아래 버튼을 눌러 앱으로 이동해 주세요.'
              : '앱으로 이동합니다...'}
          </SuccessMessage>
        </SuccessContainer>
      </CaresenseLayout>
    );
  }

  return (
    <CaresenseLayout>
      <CaresenseLoading progress={progress} />
    </CaresenseLayout>
  );
}
